import { useState, useMemo } from 'react';
import { Event } from '../../../types/event';
import { Tabs } from '../../../Components/Tabs/Tabs';
import noImage from '../../../Assets/Images/no-img.jpg';
import { formatDateRange } from '../../../utils/dateFormatters';
import DOMPurify from 'dompurify';
import { useFormatImageUrl } from '../../../utils/formatImageUrl';
import './EventDetails.scss';

type TabId = 'tab1' | 'tab2' | 'tab3';

interface EventDetailsProps {
  event: Event;
}

export const EventDetails = ({ event }: EventDetailsProps) => {
  const formatImageUrl = useFormatImageUrl();

  const tabs = useMemo(() => {
    const availableTabs = [];

    if (event.option1) {
      availableTabs.push({ id: 'tab1' as TabId, label: 'Tab 1' });
    }
    if (event.option2) {
      availableTabs.push({ id: 'tab2' as TabId, label: 'Tab 2' });
    }
    if (event.option3) {
      availableTabs.push({ id: 'tab3' as TabId, label: 'Tab 3' });
    }

    return availableTabs;
  }, [event.option1, event.option2, event.option3]);

  const [activeTab, setActiveTab] = useState<TabId>(tabs[0]?.id || 'tab1');

  const decodeHtmlEntities = (html: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = html;
    return textArea.value;
  };

  const sanitizeHTML = (html: string) => {
    if (!html) return '';

    let processedHtml = html;

    // First check if the content contains raw HTML tags as text
    if (processedHtml.includes('&lt;') && processedHtml.includes('&gt;')) {
      // If it does, decode HTML entities first
      processedHtml = decodeHtmlEntities(processedHtml);

      // At this point, we should have raw HTML without encoded entities
      // We need to check if we need to wrap it in a tag to make it valid HTML
      if (!processedHtml.trim().startsWith('<')) {
        processedHtml = `<div>${processedHtml}</div>`;
      }
    }

    return DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'span', 'img', 'a', 'div'],
      ALLOWED_ATTR: ['style', 'class', 'src', 'alt', 'href', 'target'],
    });
  };

  const renderTabContent = () => {
    let content = '';

    switch (activeTab) {
      case 'tab1':
        content = event.option1 || '';
        break;
      case 'tab2':
        content = event.option2 || '';
        break;
      case 'tab3':
        content = event.option3 || '';
        break;
      default:
        return null;
    }

    if (!content) return null;

    return (
      <div className="tab-content" dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
    );
  };

  return (
    <div className="event-details">
      <img className="event-image" src={formatImageUrl(event.image) || noImage} alt={event.name} />
      <div className="content">
        <h1 className="title">{event.name}</h1>
        <div className="date">{formatDateRange(event.startDate, event.endDate)}</div>
        <div className="description">{event.description}</div>
        {tabs.length > 0 && (
          <>
            <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            {renderTabContent()}
          </>
        )}
      </div>
    </div>
  );
};
